/**
 * Tailwind CSS implementation of Avatar component
 * Uses Seeds avatar CSS classes from avatar.css
 */

import React, { useState, useCallback, useMemo, useEffect } from "react";

import Image from "@sproutsocial/seeds-react-image";
import Text from "@sproutsocial/seeds-react-text";
import type { TypeAvatarProps } from "./AvatarTypes";

import { getInitials, getAvatarColor } from "./utils";

const defaultType = "neutral";

// Utility for merging class names properly
function cn(
  ...inputs: (string | undefined | null | false | Record<string, boolean>)[]
): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === "string") {
      classes.push(input);
    } else if (typeof input === "object") {
      for (const [key, value] of Object.entries(input)) {
        if (value) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(" ");
}

export const AvatarTailwind = React.forwardRef<HTMLDivElement, TypeAvatarProps>(
  (
    {
      appearance = "circle",
      name = "",
      src,
      type = defaultType,
      size = "40px",
      // `bg`/`color` are destructured only to keep them off the DOM div — they
      // normally never reach this path (they route to the styled-components path).
      bg,
      color,
      initials,
      "aria-hidden": ariaHidden,
      className,
      ...rest
    }: TypeAvatarProps,
    ref
  ) => {
    const colorType = getAvatarColor(name, type);
    const [imageFailedLoading, setImageFailedLoading] = useState(false);

    useEffect(() => {
      // If the src changes, we need to invalidate the image failed to load flag
      setImageFailedLoading(false);
    }, [src]);

    const displayInitials = useMemo(
      () => initials || getInitials(name),
      [initials, name]
    );
    const handleError = useCallback(() => {
      setImageFailedLoading(true);
    }, [setImageFailedLoading]);

    // Font size for initials is half the size of the avatar, rounded down.
    const fontSize = Math.floor(Number(size.replace("px", "")) * 0.4);

    const displayFallback = !src || imageFailedLoading;

    const ariaProps = {
      "aria-hidden": ariaHidden,
      alt: ariaHidden ? "" : name,
    };

    const classes = cn(
      "seeds-avatar",
      appearance === "leaf" ? "seeds-avatar-leaf" : "seeds-avatar-circle",
      `seeds-avatar-${colorType}`,
      { "seeds-avatar-fallback": displayFallback },
      className
    );

    return (
      <div
        data-qa-user-avatar={name}
        {...(rest as React.HTMLAttributes<HTMLDivElement>)}
        ref={ref}
        className={classes}
        title={name || "?"}
        // Size is pushed through a CSS custom property consumed by avatar.css.
        // Placed after {...rest} so the size var can't be clobbered.
        style={{ "--seeds-avatar-size": size } as React.CSSProperties}
      >
        {displayFallback ? (
          <Text
            lineHeight={size}
            fontWeight="semibold"
            // font-size stays raw px (NOT the Text type-scale): apply via inline
            // style, never the fontSize prop (which maps to typeScale). The
            // decorative text color is inherited from the .seeds-avatar-<type> class.
            style={{ fontSize: `${fontSize}px` }}
            // If the image fails to load, the avatar is decorative only and should be hidden from assistive technologies.
            // There is a title of the full name
            aria-hidden
          >
            {displayInitials}
          </Text>
        ) : (
          <Image
            {...ariaProps}
            width="auto"
            height="100%"
            src={src}
            onError={handleError}
            m={0}
          />
        )}
      </div>
    );
  }
);

AvatarTailwind.displayName = "AvatarTailwind";
